import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getConversations, getMessages, startDirectConversation } from "../../modules/Chat/chatThunk";
import { getAllNurse } from "../../modules/NursignCare/ManageNurse/ManageNurseThunk";
import { addSentMessage, applyMessageAck, addIncomingMessage } from "../../modules/Chat/chatSlice";
import { getLocalizedText } from "../../utils/localization";
import authService from "../../services/authService";
import environment from "../../utils/environment";
import Icons from "../../Assets/Icons";
import "./ChatWidget.css";

const CMD_PARTICIPANT_TYPE = "cmd";

// Resolves the "other" participant (nurse/patient) in a direct conversation
const getOtherParticipant = (conversation) => {
  return (conversation.participants || []).find((p) => p.participantType !== CMD_PARTICIPANT_TYPE) || null;
};

const formatTime = (dateTime) => {
  if (!dateTime) return "";
  const d = new Date(dateTime);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

// Floating chat widget — shows a launcher icon, conversations list, and a chat window.
// `openConversation` (optional) lets a parent page jump directly into a specific
// nurse's conversation, e.g. { nurseId, name, employeeID, profilePhoto } from the
// Session Details "Homecare Staff" card.
const ChatWidget = ({ openConversation, onOpenConversationHandled }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("vn") || i18n.language?.startsWith("vi") ? "vi" : "en";
  const { conversations } = useSelector((state) => state.chat);
  const nurses = useSelector((state) => state.manageNurse?.nurses || []);

  const [isOpen, setIsOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [directParticipant, setDirectParticipant] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [draftMessage, setDraftMessage] = useState("");
  const wsRef = useRef(null);

  const messagesByConversation = useSelector((state) => state.chat.messagesByConversation);
  const activeMessages = activeConversationId ? messagesByConversation[activeConversationId] || [] : [];
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      dispatch(getConversations());
      dispatch(getAllNurse({}));
    }
  }, [isOpen, dispatch]);

  // Handle a request from a parent page to open a specific nurse's conversation directly
  useEffect(() => {
    if (!openConversation?.nurseId) return;

    setIsOpen(true);
    setDirectParticipant(openConversation);
    dispatch(getConversations()).then((result) => {
      const list = result.payload?.data?.conversations || [];
      const match = list.find((c) => getOtherParticipant(c)?.participantId === openConversation.nurseId);
      if (match) {
        setActiveConversationId(match._id);
        onOpenConversationHandled?.();
        return;
      }
      // No existing conversation — create/fetch one via the direct-conversation endpoint
      dispatch(startDirectConversation({ targetId: openConversation.nurseId, targetType: "nurse" })).then((res) => {
        const conversation = res.payload?.data?.conversation || res.payload?.data;
        if (conversation?._id) setActiveConversationId(conversation._id);
        onOpenConversationHandled?.();
      });
    });
  }, [openConversation, dispatch, onOpenConversationHandled]);

  useEffect(() => {
    if (activeConversationId) dispatch(getMessages({ conversationId: activeConversationId }));
  }, [activeConversationId, dispatch]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeMessages.length]);

  // Open a websocket connection while the chat window is open, used for sending messages in real time
  useEffect(() => {
    if (!isOpen) return;

    const token = authService.getToken();
    const wsBase = environment.wsURL || "ws://backend-stage.fvhospital.com";
    const socket = new WebSocket(`${wsBase}?token=${token}&role=cmd`);
    wsRef.current = socket;

    socket.onopen = () => console.log("[chat-ws] connected");
    socket.onclose = (e) => console.log("[chat-ws] closed", e.code, e.reason);
    socket.onerror = (e) => console.error("[chat-ws] error", e);
    socket.onmessage = (e) => {
      console.log("[chat-ws] message received", e.data);
      let parsed;
      try {
        parsed = JSON.parse(e.data);
      } catch {
        return;
      }

      if (parsed.event === "MESSAGE_ACK") {
        const { clientMsgId, serverMsgId, conversationId, createdAt } = parsed.payload || {};
        dispatch(applyMessageAck({ conversationId, clientMsgId, serverMsgId, createdAt }));
        return;
      }

      // Unknown event — log it so we can confirm the real event name/shape for incoming messages.
      // Best-effort: if it looks like a message (has conversationId + text), add it to the thread.
      console.warn("[chat-ws] unhandled event:", parsed.event, parsed.payload);
      const candidate = parsed.payload;
      if (candidate?.conversationId && candidate?.text) {
        dispatch(
          addIncomingMessage({
            conversationId: candidate.conversationId,
            message: {
              _id: candidate._id || candidate.serverMsgId || `incoming-${Date.now()}`,
              contentType: candidate.contentType || "text",
              text: candidate.text,
              senderType: candidate.senderType || "nurse",
              createdAt: candidate.createdAt || new Date().toISOString(),
            },
          }),
        );
      }
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [isOpen, dispatch]);

  const activeConversation = conversations.find((c) => c._id === activeConversationId) || null;

  // Look up a nurse's name/photo/employeeID from the loaded nurse list by participantId
  const getNurseInfo = (nurseId) => nurses.find((n) => n._id === nurseId);

  const getNurseName = (nurse) => {
    if (!nurse) return "";
    const first = getLocalizedText(nurse.firstName, lang);
    const last = getLocalizedText(nurse.lastName, lang);
    return `${first} ${last}`.trim();
  };

  // Resolves display info (photo, name, subtitle id) for the other participant in a conversation
  const getParticipantInfo = (conversation) => {
    const other = getOtherParticipant(conversation);
    if (!other) return { name: t("unknown") || "Unknown", subtitle: "", photo: null };

    if (other.participantType === "nurse") {
      const nurse = getNurseInfo(other.participantId);
      if (nurse) {
        return { name: getNurseName(nurse) || `Nurse #${other.participantId.slice(-6)}`, subtitle: nurse.employeeID || "", photo: nurse.profilePhoto };
      }
      return { name: `Nurse #${other.participantId.slice(-6)}`, subtitle: "", photo: null };
    }

    if (other.participantType === "user") {
      return { name: `Patient #${other.participantId.slice(-6)}`, subtitle: "", photo: null };
    }

    return { name: `User #${(other.participantId || "").slice(-6)}`, subtitle: "", photo: null };
  };

  // Header info for the active chat window — uses directParticipant when opened via "Chat with Nurse"
  const activeHeaderInfo = useMemo(() => {
    if (activeConversation) return getParticipantInfo(activeConversation);
    if (directParticipant) {
      return { name: directParticipant.name || `Nurse #${directParticipant.nurseId.slice(-6)}`, subtitle: directParticipant.employeeID || "", photo: directParticipant.profilePhoto };
    }
    return { name: "", subtitle: "", photo: null };
  }, [activeConversation, directParticipant]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredConversations = conversations.filter((conversation) => {
    if (!searchTerm.trim()) return true;
    const { name } = getParticipantInfo(conversation);
    return name.toLowerCase().includes(searchTerm.trim().toLowerCase());
  });

  const handleSend = () => {
    const text = draftMessage.trim();
    if (!text || !activeConversationId) return;

    const clientMsgId = crypto.randomUUID();
    const payload = { event: "SEND_MESSAGE", payload: { conversationId: activeConversationId, contentType: "text", text, clientMsgId } };
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("[chat-ws] sending", payload);
      wsRef.current.send(JSON.stringify(payload));
    } else {
      console.warn("[chat-ws] socket not open, message not sent to server. readyState:", wsRef.current?.readyState);
    }

    dispatch(
      addSentMessage({
        conversationId: activeConversationId,
        message: { _id: clientMsgId, contentType: "text", text, senderType: CMD_PARTICIPANT_TYPE, createdAt: new Date().toISOString() },
      }),
    );
    setDraftMessage("");
  };

  const closeWidget = () => {
    setIsOpen(false);
    setActiveConversationId(null);
    setDirectParticipant(null);
    setSearchTerm("");
  };

  const goBackToList = () => {
    setActiveConversationId(null);
    setDirectParticipant(null);
  };

  if (!isOpen) {
    return (
      <button className="chat-widget-launcher" onClick={() => setIsOpen(true)} aria-label={t("chat") || "Chat"}>
        <img src={Icons.chatIcon} alt="chat" className="chat-widget-launcher-icon" />
      </button>
    );
  }

  return (
    <div className="chat-widget-panel">
      {activeConversationId ? (
        <div className="chat-window">
          <div className="chat-window-header">
            <button className="chat-back-btn" onClick={goBackToList}>
              ←
            </button>
            {activeHeaderInfo.photo ? (
              <img src={activeHeaderInfo.photo} alt={activeHeaderInfo.name} className="chat-header-avatar-img" />
            ) : (
              <div className="chat-header-avatar">{activeHeaderInfo.name.charAt(0) || "?"}</div>
            )}
            <div className="chat-window-title-group">
              <span className="chat-window-title">{activeHeaderInfo.name}</span>
              {activeHeaderInfo.subtitle && <span className="chat-window-subtitle">{activeHeaderInfo.subtitle}</span>}
            </div>
            <button className="chat-close-btn" onClick={closeWidget}>
              ✕
            </button>
          </div>
          <div className="chat-window-messages">
            {activeMessages.map((msg) => (
              <div key={msg._id} className={`chat-bubble-row ${msg.senderType === CMD_PARTICIPANT_TYPE ? "outgoing" : "incoming"}`}>
                <div className="chat-bubble">
                  <p className="chat-bubble-text">{msg.text}</p>
                  <span className="chat-bubble-time">{formatTime(msg.createdAt)}</span>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-window-input-row">
            <input
              type="text"
              className="chat-input"
              placeholder={t("typeAMessage") || "Type a message..."}
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button className="chat-send-btn" onClick={handleSend} disabled={!draftMessage.trim()}>
              ➤
            </button>
          </div>
        </div>
      ) : (
        <div className="chat-list">
          <div className="chat-list-header">
            <div className="chat-list-search">
              <span className="chat-search-icon">🔍</span>
              <input
                type="text"
                className="chat-search-input"
                placeholder={t("search") || "Search"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="chat-close-btn chat-close-btn-overlay" onClick={closeWidget}>
              ✕
            </button>
          </div>
          <div className="chat-list-title-row">
            <span className="chat-list-title">{t("chats") || "Chats"}</span>
          </div>
          <div className="chat-list-items">
            {filteredConversations.length === 0 && <div className="chat-list-empty">{t("noConversations") || "No conversations yet"}</div>}
            {filteredConversations.map((conversation) => {
              const { name, subtitle, photo } = getParticipantInfo(conversation);
              const unreadCount = conversation.unreadCount;
              return (
                <div key={conversation._id} className="chat-list-item" onClick={() => setActiveConversationId(conversation._id)}>
                  {photo ? (
                    <img src={photo} alt={name} className="chat-list-avatar-img" />
                  ) : (
                    <div className="chat-list-avatar">{name.charAt(0) || "?"}</div>
                  )}
                  <div className="chat-list-info">
                    <span className="chat-list-name">{name}</span>
                    <span className="chat-list-preview">{conversation.lastMessage?.text || subtitle || t("noMessagesYet") || "No messages yet"}</span>
                  </div>
                  <div className="chat-list-meta">
                    <span className="chat-list-time">{formatTime(conversation.lastMessageAt)}</span>
                    {!!unreadCount && <span className="chat-unread-badge">{unreadCount}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
